import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

interface StatsCardProps {
  title: string;
  value: string | number;
  description?: string;
  icon: LucideIcon;
  variant?: 'default' | 'primary' | 'success';
  progress?: number;
  className?: string;
  onClick?: () => void;
}

export function StatsCard({ 
  title, 
  value, 
  description, 
  icon: Icon, 
  variant = 'default',
  progress,
  className,
  onClick
}: StatsCardProps) {
  const variants = {
    default: "bg-gradient-card border shadow-md hover:shadow-lg",
    primary: "bg-gradient-primary text-primary-foreground border-0 shadow-glow",
    success: "bg-gradient-success text-success-foreground border-0 shadow-lg"
  };

  return (
    <Card 
      className={cn(
        "transition-all duration-300 hover:scale-105 transform",
        onClick && "cursor-pointer hover:shadow-xl",
        variants[variant],
        className
      )}
      onClick={onClick}
    >
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className={cn(
          "text-sm font-medium",
          variant === 'default' ? "text-muted-foreground" : "opacity-90"
        )}>
          {title}
        </CardTitle>
        <Icon className={cn(
          "h-5 w-5",
          variant === 'default' ? "text-muted-foreground" : "opacity-90"
        )} />
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold mb-2">{value}</div>
        {progress !== undefined && (
          <div className={cn(
            "w-full rounded-full h-2 mb-2",
            variant === 'primary' ? "bg-primary-foreground/30" : "bg-muted"
          )}>
            <div
              className={cn(
                "h-2 rounded-full transition-all duration-700 ease-out",
                variant === 'primary' ? "bg-primary-foreground" : "bg-primary"
              )}
              style={{ width: `${Math.min(100, progress)}%` }}
            />
          </div>
        )}
        {description && (
          <p className={cn(
            "text-xs",
            variant === 'default' ? "text-muted-foreground" : "opacity-90"
          )}>
            {description}
          </p>
        )}
      </CardContent>
    </Card>
  );
}